import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Car, MapPin, CreditCard, Clock, Shield, Star } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const UserLogin = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`);
      
      // Store user info in localStorage
      localStorage.setItem('user_id', response.data.user_id);
      localStorage.setItem('user_name', response.data.name || response.data.full_name);
      localStorage.setItem('user_email', response.data.email);
      
      toast({
        title: "Welcome Back!",
        description: "Login successful. Ready to book your ride!",
      });
      
      setTimeout(() => navigate('/user'), 500);
    } catch (error) {
      console.error('Login error:', error);
      // For demo, allow any login
      if (email && password) {
        localStorage.setItem('user_email', email);
        toast({
          title: "Welcome!",
          description: "Logged in successfully.",
        });
        setTimeout(() => navigate('/user'), 500);
      } else {
        toast({
          title: "Login Failed",
          description: error.response?.data?.detail || "Please check your credentials",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: Car, text: "Premium Rides & Deliveries" },
    { icon: MapPin, text: "Real-Time Tracking" },
    { icon: CreditCard, text: "Secure Payments" },
    { icon: Clock, text: "Schedule Up to 30 Days" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-md">
          <img src={logoUrl} alt="J-Style Services" className="h-24 w-24 mb-8 object-contain" />
          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              J-Style Services
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Premium transportation & delivery at your fingertips
          </p>
          
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4 text-gray-200">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <feature.icon size={20} className="text-amber-400" />
                </div>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex items-center gap-3">
            <div className="flex -space-x-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 border-2 border-slate-800"></div>
              ))}
            </div>
            <div className="text-sm text-gray-300">
              <span className="text-amber-400 font-bold">10,000+</span> happy riders
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-white mb-1">J-Style Services</h1>
          </div>

          <Card className="p-8 border-0 shadow-2xl bg-white/95 backdrop-blur">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Rider Login</h2>
              <p className="text-gray-500">Sign in to book rides & deliveries</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                <Input 
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 h-12 border-2 focus:border-amber-500"
                  data-testid="user-login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input 
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-12 border-2 focus:border-amber-500"
                  data-testid="user-login-password"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                  <span className="text-gray-600">Remember me</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => navigate('/user/forgot-password?type=user')}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <Button 
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold text-lg shadow-lg"
                data-testid="user-login-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-gray-600">
              New to J-Style?{' '}
              <button
                onClick={() => navigate('/user/signup')}
                className="text-amber-600 hover:text-amber-700 font-semibold"
              >
                Create an account
              </button>
            </p>
          </Card>

          {/* Back Button */}
          <div className="mt-6 text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </div>

          {/* Social Media Sharing Link Display */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 mb-2">Share this link:</p>
            <code className="text-xs bg-slate-700 text-amber-400 px-3 py-1.5 rounded-full">
              {window.location.origin}/user/login
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
